import { FunctionComponent, useCallback, useMemo } from 'react';
import css from './GamesPost.module.less';
import Image from '@components/Image';
import { IPromotionGames } from '@interfaces/IPromotionGames';
import useTranslate from '@hooks/useTranslate';
import componentContent from '../GamesPromotionPage.content.json';
import cn from 'classnames';
import { Tag, Tooltip } from 'antd';
import { ClockCircleOutlined, EditOutlined, StopOutlined } from '@ant-design/icons';
import { FULL_TIME_FORMAT } from '@config/format';
import moment from 'moment';
import {
  allowEdit,
  chooseStatusColor,
  chooseStatusText,
  promotionIsActive,
} from './GamesPost.helpers';
import { openModal } from '@stores/modals';
import { finishPromotion } from '@stores/promotion';
interface GamePostProps {
  promotion: IPromotionGames;
}

const GamePost: FunctionComponent<GamePostProps> = ({ promotion }) => {
  const content = useTranslate(componentContent);

  const timeBlock = useMemo(() => {
    const { starts, ends } = promotion;
    return (
      <div className={css.metaLineItem}>
        <span className={css.metaIcon}>
          <ClockCircleOutlined />
        </span>
        <span>
          {`${moment(starts).format(FULL_TIME_FORMAT)} - ${
            ends ? moment(ends).format(FULL_TIME_FORMAT) : ''
          }`}
        </span>
      </div>
    );
  }, []);

  function getLocalesListTag<T extends { [key: string]: string | number | any }>(
    list: T[],
    color: string,
    size = 3,
  ): JSX.Element[] {
    const listTag =
      list &&
      list.slice(0, size).map((item, index) => (
        <Tag
          color={color}
          key={item[index]}
        >
          {item.toUpperCase()}
        </Tag>
      ));
    if (list && list.length > size) {
      const gameOver = list
        .slice(size)
        .map((item) => item.toUpperCase())
        .join(', ');

      listTag.push(
        <Tooltip
          placement='right'
          trigger='hover'
          title={gameOver}
          key='over'
        >
          <Tag color={color}>...</Tag>
        </Tooltip>,
      );
    }
    return listTag;
  }

  const locales = useMemo(() => {
    const color = 'success';
    if (promotion.filter?.locales !== null && promotion.filter?.locales.length !== 0) {
      return getLocalesListTag(promotion.filter?.locales, color);
    } else return null;
  }, [promotion?.filter?.games]);

  const editPromotion = useCallback(() => {
    openModal({ name: 'promotion', data: promotion });
  }, [promotion]);

  const finishForcedPromotion = useCallback(() => {
    finishPromotion(promotion);
  }, [promotion]);

  const allowChangePromotion = useMemo(() => {
    return allowEdit(promotion.status);
  }, [promotion.status]);

  const isActive = useMemo(() => {
    return promotionIsActive(promotion.status);
  }, [promotion.status]);

  return (
    <div className={cn(css.root, isActive && css.notActive)}>
      <div className={css.image}>
        <Image
          className={css.image}
          url={promotion?.game?.image?.url || ''}
        />
      </div>
      <div className={css.content}>
        <div className={css.header}>
          <div className='clickable'>
            {content.title} : {promotion.name}
          </div>
        </div>

        <div className={css.body}>
          <div className={css.info}>
            <span className={css.title}>{content.game}:</span>
            <Tag color='orange'>{promotion.game.name}</Tag>
          </div>

          <div className={css.info}>
            <span className={css.title}>{content.status}:</span>
            <Tag color={chooseStatusColor(promotion.status)}>
              {chooseStatusText(promotion.status, content)}
            </Tag>
          </div>

          {promotion?.filter?.locales && (
            <div className={css.info}>
              <span className={css.title}>{content.locales}:</span>
              {locales}
            </div>
          )}

          <div className={css.info}>
            <span className={css.title}>{content.position}:</span>
            <span className='clickable'>{promotion.position}</span>
          </div>
        </div>
        <div className={css.footer}>
          <div className={cn(css.metaLine, css.meta)}>
            {timeBlock}
            {isActive && (
              <div
                className={css.metaLineItem}
                onClick={finishForcedPromotion}
              >
                <span className={css.metaIcon}>
                  <StopOutlined />
                </span>
                <span className='clickable'>{content.block}</span>
              </div>
            )}
            {allowChangePromotion && (
              <div className={css.metaLineItem}>
                <span className={css.metaIcon}>
                  <EditOutlined />
                </span>
                <span
                  className='clickable'
                  onClick={editPromotion}
                >
                  {content.editing}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePost;
